import React from "react";
import { Map, Layers, Compass, Sparkles, Users, Download } from "lucide-react";
import SeoLandingTemplate from "./SeoLandingTemplate";

/**
 * /en/dnd-map-maker  ·  /it/dnd-map-maker
 *
 * SEO landing dedicated to the "D&D map maker" query cluster.  Content is
 * English-first (primary SEO target) with a natural Italian adaptation —
 * the Italian copy is not a word-for-word translation because the intent of
 * a user searching in Italian for "creatore mappe D&D" is subtly different.
 */
const data = {
  slug: "dnd-map-maker",
  path: "/dnd-map-maker",
  seoTitle: {
    en: "D&D Map Maker — build fantasy campaign maps online (Free & Premium)",
    it: "Creatore di mappe D&D — costruisci mappe fantasy per la tua campagna online",
  },
  seoDescription: {
    en: "Draw campaign maps for Dungeons & Dragons, Pathfinder and every TTRPG. Cartomancer is the free online map maker of the Mytharion ecosystem — regions, cities, dungeons, points of interest and DM-only notes.",
    it: "Disegna mappe di campagna per Dungeons & Dragons, Pathfinder e ogni GDR. Cartomancer è il creatore di mappe online dell'ecosistema Mytharion: regioni, città, dungeon, punti d'interesse e note per il Master.",
  },
  eyebrow: { en: "Mytharion · Fantasy map maker", it: "Mytharion · Creatore mappe fantasy" },
  h1: {
    en: "D&D Map Maker — the map editor built for Dungeon Masters",
    it: "Creatore di mappe D&D pensato per i Dungeon Master",
  },
  lead: [
    {
      en: "Cartomancer turns a blank page into a living campaign world. Draw regions, cities, dungeons and points of interest — then run your D&D or Pathfinder sessions with maps your players can actually explore.",
      it: "Con Cartomancer una pagina bianca diventa un mondo di campagna vivente. Disegna regioni, città, dungeon e punti d'interesse, e conduci le tue sessioni di D&D o Pathfinder con mappe che i giocatori possono davvero esplorare.",
    },
    {
      en: "Every map is layered and DM-safe: keep hidden rooms, spoilers and future plot hooks under the fog of war until the moment they matter.",
      it: "Ogni mappa è organizzata a livelli e sicura per il DM: mantieni stanze nascoste, spoiler e ganci narrativi futuri sotto la nebbia di guerra fino al momento giusto.",
    },
  ],
  primaryCta: {
    href: "/product/cartomancer_basic",
    label: { en: "Get Cartomancer — €7.99", it: "Ottieni Cartomancer — €7.99" },
  },
  secondaryCta: {
    href: "/cartomancer",
    label: { en: "See Cartomancer overview", it: "Panoramica Cartomancer" },
  },
  featuresTitle: {
    en: "Everything a D&D map maker should do",
    it: "Tutto ciò che un creatore di mappe D&D dovrebbe fare",
  },
  features: [
    {
      icon: Map,
      title: { en: "Maps at every scale", it: "Mappe a ogni scala" },
      desc: {
        en: "Continent, region, city or single-room dungeon — Cartomancer handles it all inside one project.",
        it: "Continenti, regioni, città o dungeon stanza-per-stanza. Cartomancer gestisce tutto in un unico progetto.",
      },
    },
    {
      icon: Compass,
      title: { en: "Points of interest & distances", it: "Punti d'interesse e distanze" },
      desc: {
        en: "Drop labeled markers, measure real travel distances and attach lore, NPCs or quest hooks to any location.",
        it: "Aggiungi marker etichettati, misura le distanze reali di viaggio e allega lore, NPC o ganci narrativi a ogni luogo.",
      },
    },
    {
      icon: Layers,
      title: { en: "Player & DM layers", it: "Livelli Giocatore e DM" },
      desc: {
        en: "Choose which layer the players see and which one stays yours. Fog of war reveals only what you want, when you want.",
        it: "Scegli il livello che vedono i giocatori e quello che resta tuo. La nebbia di guerra rivela solo ciò che vuoi, quando vuoi.",
      },
    },
    {
      icon: Sparkles,
      title: { en: "AI narrative journal (Premium)", it: "Diario narrativo AI (Premium)" },
      desc: {
        en: "Turn session notes into a shareable campaign chronicle without leaving the map editor.",
        it: "Trasforma gli appunti di sessione in un cronaca di campagna condivisibile senza uscire dall'editor.",
      },
    },
    {
      icon: Users,
      title: { en: "Built for D&D & Pathfinder", it: "Pensato per D&D e Pathfinder" },
      desc: {
        en: "Preset scales, distances and travel modes match how DMs actually run tabletop games.",
        it: "Scale, distanze e modalità di viaggio preimpostate riflettono come i DM conducono davvero le sessioni.",
      },
    },
    {
      icon: Download,
      title: { en: "Export to the VTT", it: "Esporta al VTT" },
      desc: {
        en: "Load any map into MytharionVTT and run the session with tokens, dice, chat and initiative.",
        it: "Carica qualsiasi mappa in MytharionVTT e conduci la sessione con token, dadi, chat e iniziativa.",
      },
    },
  ],
  how: [
    {
      title: { en: "Create your world", it: "Crea il tuo mondo" },
      desc: {
        en: "Start a new project and pick a scale — from single dungeon to whole continent.",
        it: "Apri un nuovo progetto e scegli una scala, dal singolo dungeon all'intero continente.",
      },
    },
    {
      title: { en: "Draw & annotate", it: "Disegna e annota" },
      desc: {
        en: "Add regions, cities, roads, points of interest. Every marker holds lore, NPCs, treasures and secret DM notes.",
        it: "Aggiungi regioni, città, strade, punti d'interesse. Ogni marker contiene lore, NPC, tesori e note segrete del DM.",
      },
    },
    {
      title: { en: "Play with your table", it: "Gioca con il tuo tavolo" },
      desc: {
        en: "Share what your players see, keep the rest hidden, and — when ready — jump straight into MytharionVTT.",
        it: "Condividi ciò che vedono i giocatori, tieni nascosto il resto e — quando sei pronto — passa a MytharionVTT.",
      },
    },
  ],
  benefits: [
    { en: "Free to start with Cartomancer Basic (€7.99 one-time)", it: "Parti subito con Cartomancer Basic (€7.99 una tantum)" },
    { en: "Web-based — nothing to install", it: "Basato sul web, nessuna installazione" },
    { en: "Works on desktop and tablet", it: "Funziona su desktop e tablet" },
    { en: "Cloud saved projects, quota per plan", it: "Progetti salvati sul cloud, quota per piano" },
    { en: "Integrated with the Mytharion ecosystem", it: "Integrato con l'ecosistema Mytharion" },
    { en: "Built and maintained by an active DM", it: "Costruito e mantenuto da un DM attivo" },
  ],
  faq: [
    {
      q: { en: "Is Mytharion a D&D map maker for online play?", it: "Mytharion è un creatore di mappe D&D per il gioco online?" },
      a: {
        en: "Yes. Cartomancer is the map editor and MytharionVTT is the online Virtual Tabletop. You draw your world in Cartomancer and run the sessions in the VTT.",
        it: "Sì. Cartomancer è l'editor di mappe e MytharionVTT è il Virtual Tabletop online. Disegni il mondo in Cartomancer e conduci le sessioni nel VTT.",
      },
    },
    {
      q: { en: "Can I hide areas from my players?", it: "Posso nascondere aree ai miei giocatori?" },
      a: {
        en: "Yes. Every marker has a DM-only notes field and each layer can be player-hidden. Fog of war reveals areas only when you say so.",
        it: "Sì. Ogni marker ha un campo note privato per il DM e ogni livello può essere nascosto ai giocatori. La nebbia di guerra rivela le aree solo quando vuoi tu.",
      },
    },
    {
      q: { en: "Do I need drawing skills?", it: "Servono capacità di disegno?" },
      a: {
        en: "No. Cartomancer uses map-shaped tools (regions, roads, cities, points of interest) so you focus on your world, not on being an artist.",
        it: "No. Cartomancer usa strumenti pensati per le mappe (regioni, strade, città, punti d'interesse) così ti concentri sul mondo, non sull'essere un artista.",
      },
    },
    {
      q: { en: "Which games does it support?", it: "Quali giochi supporta?" },
      a: {
        en: "Anything grid- or hex-based. Dungeons & Dragons 5e, Pathfinder, OSR games, homebrew systems — the map is system-agnostic.",
        it: "Qualsiasi gioco basato su griglia o esagoni. Dungeons & Dragons 5e, Pathfinder, OSR, sistemi homebrew — la mappa è indipendente dal sistema.",
      },
    },
  ],
  finalCta: {
    href: "/product/cartomancer_basic",
    headline: {
      en: "Start drawing your world in the next 5 minutes",
      it: "Inizia a disegnare il tuo mondo nei prossimi 5 minuti",
    },
    label: { en: "Get Cartomancer", it: "Ottieni Cartomancer" },
  },
  software: { name: "Cartomancer", category: "DesignApplication" },
};

export default function DndMapMakerLanding() {
  return <SeoLandingTemplate data={data} />;
}
